"use client";

import { useCallback, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { Tomorrow } from "next/font/google";
import { Logo } from "@/components/ui/logo";

// Defer three.js + r3f + drei out of the initial JS bundle. The hero text
// is the LCP element — the WebGL canvas hydrates after first paint. A neutral
// poster div paints first so there's no flash of black.
const DissolveImageSlideshow = dynamic(
  () =>
    import("@/components/ui/DissolveImageSlideshow").then(
      (m) => m.DissolveImageSlideshow
    ),
  {
    ssr: false,
    loading: () => (
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #d7dae0 0%, #bfc3cb 45%, #a7abb5 100%)",
        }}
      />
    ),
  }
);

type MediaItem = { type: "video" | "image"; src: string; alt?: string };

const geist = Tomorrow({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const pad2 = (n: number) => String(n).padStart(2, "0");

export default function TodaySection({
  mediaConfig,
  onAboutClick,
  onReady,
}: {
  mediaConfig: { items: MediaItem[]; timePerSlide: number };
  onAboutClick?: () => void;
  onReady?: () => void;
}) {
  const imageItems = mediaConfig.items.filter((item) => item.type === "image");
  const total = Math.max(imageItems.length, 1);
  const [slideIndex, setSlideIndex] = useState(0);
  const [bgReady, setBgReady] = useState(false);
  // Wash hides on a fixed timer so Lighthouse sees the page stabilize quickly
  // regardless of how long the slideshow textures take to load. bgReady is
  // an additional early-exit trigger if the network is fast.
  const [washElapsed, setWashElapsed] = useState(false);
  const counter = `${pad2(Math.min(slideIndex, total - 1) + 1)} / ${pad2(total)}`;
  const firstSlide = imageItems[0]?.src;
  const washVisible = !bgReady && !washElapsed;

  useEffect(() => {
    const id = setTimeout(() => setWashElapsed(true), 2800);
    return () => clearTimeout(id);
  }, []);

  const handleSlideChange = useCallback(
    (idx: number) => {
      setSlideIndex(idx);
      setBgReady(true);
      onReady?.();
    },
    [onReady]
  );

  // Hero reveal is now CSS-only (.today-reveal in globals.css) — runs at
  // SSR paint without waiting for JS hydration, so LCP fires on the H1
  // within one frame of FCP.

  return (
    <section
      className={`${geist.className} relative w-full h-dvh overflow-hidden lg:overflow-hidden`}
      style={{
        color: "#15181f",
        background:
          "radial-gradient(1100px 800px at 10% 0%, rgba(59,110,220,0.22) 0%, transparent 55%), radial-gradient(1000px 900px at 95% 100%, rgba(20,40,90,0.28) 0%, transparent 60%), linear-gradient(180deg, #d7dae0 0%, #bfc3cb 45%, #a7abb5 100%)",
      }}
    >
      {/* Preload the first slide so the browser fetches it during HTML parse,
          parallel to the JS bundle. React 19 hoists this <link> to <head>. */}
      {firstSlide && (
        <link
          rel="preload"
          as="image"
          href={firstSlide}
          fetchPriority="high"
        />
      )}

      {/* Fast-painting background image — paints at FCP via CSS background,
          before the WebGL Canvas hydrates. The Canvas wrapper above will fade
          in on top once textures load; until then this is what the user sees,
          giving Speed Index a much earlier "fully visible" timestamp. */}
      {firstSlide && (
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${firstSlide})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}

      {/* Full-screen background slideshow — starts hidden, fades in once
          textures finish loading. Long fade duration so the context change
          behind the H1 is gradual and imperceptible (avoids a perceived
          flicker when the gradient swings to the slideshow image). */}
      <div
        className={`absolute inset-0 z-0 overflow-hidden transition-opacity ease-out duration-[2500ms] ${
          bgReady ? "opacity-100" : "opacity-0"
        }`}
      >
        <DissolveImageSlideshow
          items={imageItems}
          timePerSlide={mediaConfig.timePerSlide}
          onSlideChange={handleSlideChange}
        />
      </div>

      {/* Readability overlay over the slideshow */}
      <div
        className="absolute inset-0 z-5 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(215,218,224,0.55) 0%, rgba(191,195,203,0.6) 45%, rgba(167,171,181,0.7) 100%)",
        }}
      />

      {/* Sibling-matching grain wash */}
      <div
        className="absolute inset-0 z-6 pointer-events-none overflow-hidden mix-blend-overlay opacity-40"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.15), rgba(0,0,0,0.08) 60%, rgba(30,60,140,0.12))",
        }}
      />

      {/* Initial brightness wash — sits above the bg image + slideshow +
          readability + grain to heavily mute everything below so the
          first-slide preload isn't the focal point while text fades in.
          Fades out on a 2.8s timer (or earlier if the slideshow becomes
          ready before then). Timer-based so Lighthouse Speed Index sees
          the page reach its stable visual state quickly even on slow
          networks where bgReady can take 5+ seconds. */}
      <div
        className={`absolute inset-0 z-7 pointer-events-none transition-opacity ease-out duration-[1200ms] ${
          washVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background:
            "linear-gradient(180deg, rgba(245,247,250,0.92) 0%, rgba(225,230,240,0.94) 45%, rgba(205,212,225,0.95) 100%)",
        }}
      />

      {/* Slideshow chrome — vertical spine + counter + brand-blue bottom bar */}
      {/* <span
        className="hidden lg:inline absolute top-6 left-6 z-10 text-[10px] font-semibold tracking-[0.45em] uppercase text-white/90 pointer-events-none"
        style={{ writingMode: "vertical-rl" }}
      >
        THE NOW &middot; MMXXVI
      </span> */}
      <span className="hidden lg:inline absolute top-6 right-6 z-10 text-[11px] font-semibold tracking-[0.32em] uppercase text-white/85 tabular-nums pointer-events-none">
        {counter}
      </span>
      <div
        className="absolute left-0 right-0 bottom-0 h-[3px] z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, #6b8bff 0%, #2b4fd6 50%, #1e3a8a 100%)",
        }}
      />

      {/* Page content */}
      <div className="relative z-20 w-full overflow-hidden px-6 lg:px-20 pt-80 lg:pt-24 pb-32 lg:pb-8 lg:h-full lg:flex lg:flex-col">
        {/* Mobile logo top */}
        <div className="lg:hidden absolute right-4 top-4">
          <Logo className="w-fit" imageClassName="w-32 h-auto" />
        </div>

        {/* Mobile contact — top left */}
        <a
          href="mailto:JOSEPH@TMRWPR.COM"
          className="nav-reveal opacity-0 lg:hidden absolute left-4 top-4 text-nav whitespace-nowrap px-4 py-3 rounded-sm bg-white text-black hover:bg-[#0009FF] transition-colors"
        >
          [ contact ]
        </a>



        {/* Eyebrow */}
        <p
          className="today-reveal mt-6 lg:mt-0 text-sm uppercase tracking-[0.3em] text-[#1e3a8a] font-semibold"
          style={{ "--reveal-delay": "0.7s" } as React.CSSProperties}
        >
          LET&apos;S BUILD TOMORROW
        </p>

        {/* Display headline — LCP element. Base delay lets the page show a
            clean gradient state before content fades in. Opacity still
            crosses 0 within one frame of the animation start, so LCP fires
            around delay + 1 frame. */}
        <h1
          className="today-reveal mt-4 lg:mt-6 font-semibold leading-[0.88] tracking-[-0.045em] text-5xl sm:text-8xl lg:text-[7.5rem] xl:text-[7.5rem] text-neutral-900 lg:max-w-[58%]"
          style={{ "--reveal-delay": "0.8s" } as React.CSSProperties}
        >
          IN A WORLD
          <br />
          OF NOISE
        </h1>

        {/* Asymmetric divider — short on desktop, full-width on mobile */}
        <div
          className="today-reveal mt-2 lg:mt-14 h-px w-full lg:w-[52%] bg-linear-to-r from-transparent via-neutral-500/50 to-transparent"
          style={{ "--reveal-delay": "0.95s" } as React.CSSProperties}
        />

        {/* Body */}
        <div
          className="today-reveal mt-2 max-w-xl"
          style={{ "--reveal-delay": "1.05s" } as React.CSSProperties}
        >
          <p className="text-lg lg:text-2xl leading-snug text-neutral-700 font-normal lg:whitespace-nowrap">
            We champion the{" "}
            <span className="text-[#1e3a8a] font-semibold">
              voices shaping tomorrow&apos;s culture
            </span>
            .
          </p>
        </div>

        {/* Mobile About — sits under the body copy */}
        {/* <div className="today-reveal opacity-0 lg:hidden mt-16 flex justify-center">
          <button
            type="button"
            onClick={onAboutClick}
            className="text-nav px-2 w-24 py-3 rounded-sm bg-white text-black hover:bg-white hover:text-black transition-colors"
          >
            [ About ]
          </button>
        </div> */}

        {/* Desktop spacer pushes the logo to the bottom of the viewport */}
        <div className="hidden lg:block flex-1" />

        {/* Logo bottom (desktop and mobile) */}
        <div
          className="today-reveal mt-12 hidden lg:block lg:mt-0"
          style={{ "--reveal-delay": "1.2s" } as React.CSSProperties}
        >
          <Logo className="w-fit" imageClassName="w-48 h-auto lg:w-64" />
        </div>
      </div>
    </section>
  );
}
